/**
 * Default sort direction for columns without an explicit defaultSortDirection.
 */
const DEFAULT_DIRECTION = 1;

/**
 * Returns the default sort state using the first sortable column.
 *
 * Convention: the leftmost (first) sortable column is the default sort.
 * If that column has `defaultSortDirection`, it is respected; otherwise
 * ascending is used.
 *
 * @param {Array<{ key?: string, getValue?: Function, sortable?: boolean, defaultSortDirection?: -1|1 }>} columns
 * @returns {{ key: string | null, direction: 1 | -1 }}
 */
export function getDefaultSort(columns) {
	const col = (columns ?? []).find((c) => c.getValue && c.sortable !== false)
		?? columns?.[0];
	return {
		key: col?.key ?? null,
		direction: /** @type {1|-1} */ (col?.defaultSortDirection ?? DEFAULT_DIRECTION),
	};
}

/**
 * Generic value comparator for sorting.
 *
 * Numeric values compare numerically; everything else compares as
 * strings using localeCompare with numeric + case-insensitive options.
 *
 * @param {unknown} a
 * @param {unknown} b
 * @returns {number}
 */
export function compareValues(a, b) {
	if (typeof a === 'number' && typeof b === 'number') {
		return a - b;
	}
	const aStr = String(a ?? '');
	const bStr = String(b ?? '');
	return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Comparator that supports tie-breaking.
 *
 * Primary comparison uses `getValue`; if the values are equal and a
 * tiebreaker callback is provided, it falls through to a secondary
 * comparison.  When `tieBreaker.direction` is omitted, the primary
 * `direction` is reused so that ascending/descending is consistent.
 *
 * @param {unknown} a
 * @param {unknown} b
 * @param {(item: unknown) => unknown} getValue
 * @param {number} direction
 * @param {{ getValue?: (item: unknown) => unknown, direction?: number }} [tieBreaker]
 * @returns {number}
 */
export function compareWithTieBreaker(a, b, getValue, direction, tieBreaker) {
	const cmp = compareValues(getValue(a), getValue(b));
	if (cmp !== 0) {
		return cmp * direction;
	}
	if (!tieBreaker || !tieBreaker.getValue) {
		return 0;
	}
	const tieDir = tieBreaker.direction ?? direction;
	return compareValues(tieBreaker.getValue(a), tieBreaker.getValue(b)) * tieDir;
}
